/** Public inquiry status check (route: /inquiry/status/:token). Anonymous, token-gated;
 *  shows only the inquiry's own status — no internal notes, assignee, or other PII. */
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader2, PackageSearch, CheckCircle2 } from 'lucide-react';
import { getInquiryStatus } from './inquiryApi';
import { cn } from '../ui';
import { Seo } from '../lib/seo';

const STEPS = ['NEW', 'ASSIGNED', 'CONTACTED', 'CONVERTED'] as const;
const LABEL: Record<string, string> = {
  NEW: 'Received', ASSIGNED: 'Assigned to a sales person', CONTACTED: 'Contacted', CONVERTED: 'Converted', CLOSED: 'Closed',
};

export function InquiryStatusPage() {
  const { token = '' } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['inquiry-status', token],
    queryFn: () => getInquiryStatus(token),
    enabled: !!token,
    retry: false,
  });

  if (isLoading) {
    return <div className="mx-auto grid max-w-2xl place-items-center px-4 py-24 text-muted"><Loader2 className="size-7 animate-spin" /></div>;
  }
  if (isError || !data) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center sm:px-6">
        <span className="mx-auto grid size-14 place-items-center rounded-full bg-surface-2 text-muted"><PackageSearch className="size-7" /></span>
        <p className="mt-4 text-lg font-bold text-content">Inquiry not found</p>
        <p className="mt-1 text-sm text-muted">This tracking link looks invalid or expired.</p>
        <Link to="/catalog" className="mt-4 inline-block text-sm font-bold text-brand-700! hover:underline">Browse products</Link>
      </div>
    );
  }

  const isClosed = data.status === 'CLOSED';
  const activeIdx = STEPS.indexOf(data.status as any);

  return (
    <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
      <Seo noindex title="Inquiry status" description="Track the status of your product inquiry with Green Bay Biotech." />
      <div className="rounded-card border border-line bg-surface p-7 shadow-soft">
        <div className="text-[12px] font-bold uppercase tracking-[0.14em] text-brand-600">Inquiry status</div>
        <h1 className="mt-1 text-2xl font-extrabold text-brand-800! dark:text-brand-300!">{data.inquiryNo}</h1>
        <p className="mt-1 text-sm text-muted">
          {data.productName ? <>For <b className="text-content">{data.productName}</b> · </> : null}
          Raised by {data.customerName} · {new Date(data.createdAt).toLocaleDateString()}
        </p>

        {isClosed ? (
          <div className="mt-6 rounded-[12px] border border-line bg-surface-2/60 p-4 text-sm text-muted">This inquiry has been closed.</div>
        ) : (
          <ol className="mt-7 space-y-4">
            {STEPS.map((s, i) => {
              const done = i < activeIdx || data.status === 'CONVERTED';
              const current = i === activeIdx;
              return (
                <li key={s} className="flex items-center gap-3">
                  <span className={cn('grid size-7 shrink-0 place-items-center rounded-full border text-[12px] font-bold',
                    done ? 'border-brand-600 bg-brand-600 text-white' : current ? 'border-brand-600 text-brand-700 dark:text-brand-300' : 'border-line text-muted')}>
                    {done ? <CheckCircle2 className="size-4" /> : i + 1}
                  </span>
                  <span className={cn('text-[15px]', current ? 'font-bold text-content' : done ? 'text-content' : 'text-muted')}>{LABEL[s]}</span>
                  {current && <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-semibold text-brand-700 dark:bg-brand-900/30 dark:text-brand-300">Current</span>}
                </li>
              );
            })}
          </ol>
        )}

        <div className="mt-7 border-t border-line pt-5">
          <Link to="/catalog" className="text-sm font-bold text-brand-700! hover:underline">← Browse more products</Link>
        </div>
      </div>
    </div>
  );
}
